import * as React from 'react';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';

import {useTranslation} from 'common/i18n/hooks';

import {WarningReact} from '../warning';
import {WarningTheme} from '../warning/constants';
import style from './index.module.styl';

type FallbackProps = {
  className?: string;
  fullScreen?: boolean;
  minimal?: boolean;
};

function Fallback({fullScreen, minimal, className}: FallbackProps) {
  const {t} = useTranslation();
  return (
    <WarningReact
      {...{
        theme: WarningTheme.Error,
        headerText: minimal ? undefined : t(`error-boundary.header`),
        bodyText: minimal ? t(`error-boundary.body-minimal`) : t(`error-boundary.body`),
        small: minimal,
        className: clsx(style.fillContainer, {[style.fullScreen]: fullScreen}, className),
      }}
    />
  );
}

type ReactErrorBoundaryProps = React.PropsWithChildren & {
  className?: string;
  fallback?: (error: any) => React.ReactNode;
  fullScreen?: boolean;
  minimal?: boolean;
  errorType?: string;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
};

export function ReactErrorBoundary(props: ReactErrorBoundaryProps) {
  const fallback = <Fallback fullScreen={props.fullScreen} minimal={props.minimal} className={props.className} />;

  const beforeCapture = React.useCallback(
    (scope: Sentry.Scope) => {
      // Add contextual tags for better Sentry categorization
      if (props.errorType) {
        scope.setTag(`boundary_type`, props.errorType);
      }
    },
    [props.errorType],
  );

  const handleError = React.useCallback(
    (error: unknown, componentStack: string | undefined, _eventId: string) => {
      try {
        props.onError?.(error as Error, {componentStack: componentStack ?? ``});
      } catch {
        // Swallow errors from onError callback to prevent error-ing in the error boundary
      }
    },
    [props],
  );

  return (
    <Sentry.ErrorBoundary fallback={fallback} beforeCapture={beforeCapture} onError={handleError}>
      {props.children}
    </Sentry.ErrorBoundary>
  );
}
